import React, { FC, useState, useRef } from 'react'
import { AnchorLinksProps } from './AnchorLinks.types'
import { sanitizeHTML } from '../../../utils/common-utils'

export const AnchorLinks: FC<AnchorLinksProps> = props => {
  const { content, testId } = props
  const { columns, backgroundColor, title, textContent, layoutSize = 'u-size10of12' } = content

  const [isSidePanelVisible, setIsSidePanelVisible] = useState(false)
  const containerRef = useRef(null)
  const toggleVisibility = () => setIsSidePanelVisible(prevState => !prevState)
  const handleAnchorClick = (event: any, toggleMobileSidePanel = false) => {
    if (event.target.tagName === 'A') {
      addSmoothScroll(event)
      toggleMobileSidePanel && toggleVisibility()
    }
  }

  const addSmoothScroll = (e: any) => {
    const href = e.target.getAttribute('href')
    const targetId = href?.replace(/^#/, '')
    const targetElement = targetId && document.getElementById(targetId)
    if (targetElement) {
      e.preventDefault()
      targetElement.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <div className={`AnchorLinks ${layoutSize} u-marginCenter ${backgroundColor} u-marginVert6gu`}>
      <div
        className={`u-sm-hidden ${columns === 'Two Columns' && 'AnchorLinks-two-columns'}`}
        data-testid={testId ? `${testId}-desktop-section` : null}>
        {<Heading title={title} icon isMobile={false} testId={testId} />}
        <div
          tabIndex={0}
          onClick={handleAnchorClick}
          className='u-paddingHoriz4gu u-paddingBottom4gu u-paddingTop2gu'
          dangerouslySetInnerHTML={{ __html: sanitizeHTML(textContent) }}
        />
      </div>

      <AnchorMobileMenu title={title} toggleVisibility={toggleVisibility} testId={testId} />

      <SidePanel
        title={title}
        isSidePanelVisible={isSidePanelVisible}
        toggleVisibility={toggleVisibility}
        textContent={textContent}
        containerRef={containerRef}
        handleAnchorClick={handleAnchorClick}
        testId={testId}
      />
    </div>
  )
}

const Heading = (headingProps?: any) => {
  const { title, isMobile, isSidePanelVisible, testId } = headingProps
  return (
    <div
      className={`${
        !isMobile &&
        'u-paddingBottom4gu u-paddingTop6gu u-paddingHoriz6gu u-marginBottom0 u-borderBottom u-borderColorNeutral'
      }`}>
      <h4
        className={`u-textPrimary u-textNormal ${
          isSidePanelVisible ? 'u-textSizeMsrp4 u-textColorNeutralGrey' : 'u-textSizeMsrp2'
        }`}
        data-testid={testId ? `${testId}-heading` : null}>
        {title || 'In this article'}

        <span
          className='adel-material-symbols-24 u-colorBrandIcon u-sm-hidden u-marginLeft2gu u-marginBottomM1gu'
          title='Link'
          aria-label='Link'>
          link
        </span>
      </h4>
    </div>
  )
}

const AnchorMobileMenu = (mobileMenuProps?: any) => {
  const { title, toggleVisibility, testId } = mobileMenuProps

  return (
    <div
      className={`AnchorLinks-mobile-container u-bgColorStone u-flex u-flexAlignItemsCenter u-paddingHoriz4gu u-md-hidden u-lg-hidden u-flexJustifyBetween`}>
      {<Heading title={title} testId={testId} isMobile />}
      <button
        className='SidePanel--trigger u-flex u-flexAlignItemsCenter'
        onClick={toggleVisibility}
        data-testid={testId ? `${testId}-open-menu` : null}>
        <span
          className='adel-material-symbols-36 u-textColorActionBlue'
          title='Open Menu'
          aria-label='Open Menu'>
          menu_open
        </span>
      </button>
    </div>
  )
}

const SidePanel = (sidePanelProps?: any) => {
  const {
    title,
    isSidePanelVisible,
    toggleVisibility,
    textContent,
    containerRef,
    handleAnchorClick,
    testId,
  } = sidePanelProps

  return (
    <aside className={`SidePanel ${isSidePanelVisible && 'is-visible'} u-md-hidden u-lg-hidden`}>
      <section
        className={`SidePanel-main ${!isSidePanelVisible && 'u-hidden'}`}
        data-testid={testId ? `${testId}-side-panel` : null}>
        <div className='SidePanel-topWrapper'>
          <header className='SidePanel-headerWrapper u-borderBottom u-borderColorNeutral'>
            <div className='SidePanel-header'>
              <div className='SidePanel-heading'>
                {
                  <Heading
                    title={title}
                    isMobile
                    isSidePanelVisible={isSidePanelVisible}
                    testId={testId}
                  />
                }
              </div>
              <button
                className={`Button Button--icon SidePanel-Button ${
                  !isSidePanelVisible && 'u-hidden'
                }`}
                onClick={toggleVisibility}
                title='Close panel'
                data-testid={testId ? `${testId}-close-panel` : null}>
                <span
                  className='adel-material-symbols-24 u-textColorActionBlue'
                  title='Close panel'
                  aria-label='Close panel'>
                  close
                </span>
              </button>
            </div>
          </header>
          <div className='SidePanel-sectionsWrapper'>
            <div
              ref={containerRef}
              onClick={e => handleAnchorClick(e, true)}
              className={`u-paddingHoriz2gu u-paddingVert2gu ${!isSidePanelVisible && 'u-hidden'}`}
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(textContent) }}
            />
          </div>
        </div>
      </section>
    </aside>
  )
}

export default AnchorLinks
